/**
 * NotificationService — creates, reads, and manages in-app
 * notifications. Integrates with Supabase Realtime for live
 * delivery, AnalyticsService for tracking, and AuditService
 * for immutable records.
 *
 * Provider-agnostic: the notification delivery channel is
 * pluggable, but in-app is always on.
 */

import { getSupabase } from '@/lib/supabase';
import { analyticsService } from '@/lib/AnalyticsService';
import { auditService } from '@/lib/AuditService';
import { LoggerFactory } from '@/lib/logging';
import type {
  NotificationRow,
  NotificationPreferences,
  CreateNotificationInput,
  NotificationListOptions,
  NotificationType,
} from './types';

const logger = LoggerFactory.create();

const DEFAULT_PREFERENCES: Omit<NotificationPreferences, 'user_id' | 'created_at' | 'updated_at'> = {
  application_status_changes: true,
  review_updates: true,
  profile_updates: true,
  announcements: true,
  system_notifications: true,
  email_digest: 'instant',
};

class NotificationServiceImpl {
  async create(input: CreateNotificationInput): Promise<NotificationRow | null> {
    const supabase = getSupabase();

    const { data, error } = await supabase
      .from('notifications')
      .insert({
        user_id: input.userId,
        type: input.type,
        title: input.title,
        body: input.body,
        priority: input.priority ?? 'normal',
        action_url: input.actionUrl ?? null,
        metadata: input.metadata ?? null,
      })
      .select()
      .maybeSingle();

    if (error) {
      logger.error('[notifications] Create failed', { error: error.message });
      return null;
    }
    if (!data) return null;

    analyticsService.track('notification_created', {
      type: input.type,
      priority: input.priority ?? 'normal',
    });

    return data as NotificationRow;
  }

  async list(userId: string, options: NotificationListOptions = {}): Promise<NotificationRow[]> {
    const supabase = getSupabase();
    const limit = options.limit ?? 20;
    const offset = options.offset ?? 0;

    let query = supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (options.unreadOnly) {
      query = query.is('read_at', null);
    }
    if (!options.includeArchived) {
      query = query.is('archived_at', null);
    }

    const { data, error } = await query;
    if (error) {
      logger.error('[notifications] List failed', { error: error.message });
      return [];
    }
    return (data ?? []) as NotificationRow[];
  }

  async getUnreadCount(userId: string): Promise<number> {
    const supabase = getSupabase();
    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .is('read_at', null)
      .is('archived_at', null);

    if (error) {
      logger.error('[notifications] Unread count failed', { error: error.message });
      return 0;
    }
    return count ?? 0;
  }

  async markRead(notificationId: string, userId: string): Promise<void> {
    const supabase = getSupabase();
    const { error } = await supabase
      .from('notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('id', notificationId)
      .eq('user_id', userId);

    if (error) {
      logger.error('[notifications] Mark read failed', { error: error.message });
    } else {
      analyticsService.track('notification_read', { notificationId });
    }
  }

  async markAllRead(userId: string): Promise<void> {
    const supabase = getSupabase();
    const { error } = await supabase
      .from('notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('user_id', userId)
      .is('read_at', null);

    if (error) {
      logger.error('[notifications] Mark all read failed', { error: error.message });
    } else {
      analyticsService.track('notification_all_read', { userId });
    }
  }

  async archive(notificationId: string, userId: string): Promise<void> {
    const supabase = getSupabase();
    const { error } = await supabase
      .from('notifications')
      .update({ archived_at: new Date().toISOString() })
      .eq('id', notificationId)
      .eq('user_id', userId);

    if (error) {
      logger.error('[notifications] Archive failed', { error: error.message });
    } else {
      await auditService.record({
        actor_id: userId,
        actor_role: 'creator',
        target_type: 'notification',
        target_id: notificationId,
        action: 'profile_update',
        previous_value: null,
        new_value: 'archived',
        notes: 'Notification archived',
      });
    }
  }

  async delete(notificationId: string, userId: string): Promise<void> {
    const supabase = getSupabase();
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId)
      .eq('user_id', userId);

    if (error) {
      logger.error('[notifications] Delete failed', { error: error.message });
    }
  }

  async getPreferences(userId: string): Promise<NotificationPreferences> {
    const supabase = getSupabase();
    const { data, error } = await supabase
      .from('notification_preferences')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error || !data) {
      return { ...DEFAULT_PREFERENCES, user_id: userId, created_at: '', updated_at: '' };
    }
    return data as NotificationPreferences;
  }

  async updatePreferences(
    userId: string,
    prefs: Partial<Omit<NotificationPreferences, 'user_id' | 'created_at' | 'updated_at'>>,
  ): Promise<void> {
    const supabase = getSupabase();

    const { error } = await supabase
      .from('notification_preferences')
      .upsert({
        user_id: userId,
        ...prefs,
        updated_at: new Date().toISOString(),
      });

    if (error) {
      logger.error('[notifications] Update preferences failed', { error: error.message });
    } else {
      analyticsService.track('notification_preferences_updated', { userId });
    }
  }

  isTypeEnabled(prefs: NotificationPreferences, type: NotificationType): boolean {
    switch (type) {
      case 'application_status_change':
        return prefs.application_status_changes;
      case 'review_update':
        return prefs.review_updates;
      case 'profile_update':
        return prefs.profile_updates;
      case 'announcement':
        return prefs.announcements;
      case 'system':
        return prefs.system_notifications;
      default:
        return true;
    }
  }
}

export const notificationService = new NotificationServiceImpl();
